import type { Tracker } from '../../types';
import {
    awaitAdvertisingPixels,
    createMissingPixelWarning,
} from '../advertising-pixels';
import { buildOpenAiEventData, type OpenAiMeasureData } from './buildEventData';
import { deriveOpenAiEvents } from './deriveOpenAiEvents';
import { isOpenAiServerForwardedEvent } from './serverForwardedEvents';

declare global {
    interface Window {
        oaiq?: (...args: unknown[]) => void;
    }
}

declare function oaiq(
    command: 'measure',
    eventName: string,
    data: OpenAiMeasureData,
    options?: { event_id?: string; custom_event_name?: string },
): void;

/**
 * Reports conversions to the ChatGPT Ads Measurement Pixel.
 *
 * Waits on the shared advertising gate so events raised before the pixel boots
 * are held rather than dropped. Carries a deduplication id only for events that
 * also get a server-side Conversions API copy.
 */
export function createOpenAiTracker(): Tracker {
    const warnPixelMissing = createMissingPixelWarning('ChatGPT Ads');

    return {
        name: 'openAiTracker',
        async log(event, opts) {
            await awaitAdvertisingPixels();

            const openAiEvents = deriveOpenAiEvents(event);
            if (openAiEvents.length === 0) return;

            const [baseEvent] = openAiEvents;

            if (typeof oaiq === 'undefined') {
                warnPixelMissing(baseEvent.eventName);
                return;
            }

            // Deduplication id is only meaningful when a server copy will arrive.
            // Carrying an id with no server counterpart would still report once
            // from the browser, but the contract is clearer when the presence of
            // an id means a server copy is coming.
            const deduplicate =
                Boolean(opts.eventId) && isOpenAiServerForwardedEvent(event);

            for (const derived of openAiEvents) {
                const data = buildOpenAiEventData(derived.dataType, opts.data);
                const options: {
                    event_id?: string;
                    custom_event_name?: string;
                } = {};

                if (deduplicate && opts.eventId) {
                    options.event_id = opts.eventId;
                }
                if (derived.isCustom) {
                    options.custom_event_name = derived.eventName;
                }

                console.log(
                    '%cOpenAiEvent: ' +
                        (derived.isCustom
                            ? `custom/${derived.eventName}`
                            : derived.eventName),
                    'background: #222; color: #10a37f',
                );

                const measureName = derived.isCustom
                    ? 'custom'
                    : derived.eventName;

                if (Object.keys(options).length > 0) {
                    oaiq('measure', measureName, data, options);
                } else {
                    oaiq('measure', measureName, data);
                }
            }
        },
        release() {
            // do nothing
        },
    };
}
